import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

/* ========================= INDUSTRIES ========================= */
const INDUSTRIES = [
  "HealthTech","FinTech","EdTech","AgriTech","ClimateTech","BioTech",
  "DeepTech","AI / ML","SaaS","E-commerce","Consumer","Gaming",
  "Web3 / Blockchain","Cybersecurity","IoT","Robotics","Mobility",
  "SpaceTech","HRTech","Other",
];

function MyReviews() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [status, setStatus] = useState("");
  const [openSection, setOpenSection] = useState("draft");

  /* ========================= REALTIME REVIEWS ========================= */
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);

    const q = query(
      collection(db, "evaluations"),
      where("evaluatorId", "==", user.uid),
      orderBy("updatedAt", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(q, async (snap) => {
      if (snap.empty) {
        setReviews([]);
        setFiltered([]);
        setLoading(false);
        return;
      }

      const founderIds = snap.docs.map((d) => d.data().founderId).filter(Boolean);
      const founderSnaps = await Promise.all(
        founderIds.map((id) => getDoc(doc(db, "users", id)))
      );

      const founderMap = {};
      founderSnaps.forEach((s) => { if (s.exists()) founderMap[s.id] = s.data(); });

      const rows = snap.docs.map((d) => {
        const data = d.data();
        const founder = founderMap[data.founderId];
        if (!founder) return null;

        return {
          evaluationId: d.id,
          founderId: data.founderId,
          startupName: founder.startupName || "Untitled startup",
          founderName: founder.fullName || "Unknown founder",
          industries: Array.isArray(founder.industries)
            ? founder.industries
            : founder.industry ? [founder.industry] : [],
          rating: typeof data.rating === "number" ? data.rating : null,
          status: data.status === "submitted" ? "submitted" : "draft",
          updatedAt: data.updatedAt || null,
        };
      }).filter(Boolean);

      setReviews(rows);
      setFiltered(rows);
      setLoading(false);
    });

    return () => unsub();
  }, [user?.uid]);

  /* ========================= FILTERING ========================= */
  useEffect(() => {
    let data = [...reviews];

    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.startupName.toLowerCase().includes(q) ||
          r.founderName.toLowerCase().includes(q)
      );
    }

    if (industry) data = data.filter((r) => r.industries?.includes(industry));
    if (status) data = data.filter((r) => r.status === status);

    setFiltered(data);
  }, [search, industry, status, reviews]);

  const draftItems = filtered.filter((r) => r.status === "draft");
  const submittedItems = filtered.filter((r) => r.status === "submitted");

  /* ========================= CARD ========================= */
  const ReviewCard = (r) => {
    const isDraft = r.status === "draft";

    return (
      <div
        key={r.evaluationId}
        className="card"
        title="This card represents one evaluation you’ve worked on."
        style={{
          padding: 18,
          marginBottom: 12,
          borderRadius: 14,
          cursor: "pointer",
          transition: "all 0.2s ease",
          border: isDraft ? "1px solid #fde68a" : "1px solid #e5e7eb",
          background: isDraft ? "#fffbeb" : "#fff",
          boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3
            style={{ marginBottom: 6 }}
            title="Startup name as provided by the founder."
          >
            {r.startupName}
          </h3>

          <span
            title={
              r.status === "submitted"
                ? "This evaluation has been submitted and can no longer be edited."
                : "This evaluation is still a draft and can be edited."
            }
            style={{
              fontSize: 12,
              padding: "5px 12px",
              borderRadius: 999,
              fontWeight: 600,
              background: r.status === "submitted" ? "#dcfce7" : "#fef3c7",
              color: r.status === "submitted" ? "#166534" : "#92400e",
              whiteSpace: "nowrap",
            }}
          >
            {r.status === "submitted" ? "Submitted" : "Draft"}
          </span>
        </div>

        <p
          style={{ fontSize: 14, color: "#64748b" }}
          title="Founder who owns this startup."
        >
          Founder: {r.founderName}
        </p>

        {r.industries.length > 0 && (
          <p
            style={{ fontSize: 12, color: "#475569" }}
            title="Industry tags selected by the founder."
          >
            {r.industries.join(", ")}
          </p>
        )}

        <p
          style={{ marginTop: 6 }}
          title="Overall rating you assigned during evaluation."
        >
          ⭐ Rating: <strong>{r.rating !== null ? `${r.rating}/5` : "—"}</strong>
        </p>

        {r.updatedAt?.toDate && (
          <p
            style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}
            title="Last time you saved or submitted this evaluation."
          >
            Last updated on {r.updatedAt.toDate().toLocaleDateString()}
          </p>
        )}

        <div style={{ marginTop: 12, display: "flex", gap: 10 }}>
          {isDraft && (
            <button
              className="btn-primary"
              title="Resume editing this draft evaluation."
              onClick={() =>
                navigate(`/evaluator/startup/${r.founderId}?mode=review`)
              }
            >
              Continue Review
            </button>
          )}
          <button
            className="btn-secondary"
            title="View the startup profile in read-only mode."
            onClick={() =>
              navigate(`/evaluator/startup/${r.founderId}?mode=view`)
            }
          >
            View Startup
          </button>
        </div>
      </div>
    );
  };

  /* ========================= UI ========================= */
  return (
    <div>
      <h1
        style={{ marginBottom: 4 }}
        title="All startups you have evaluated or started evaluating."
      >
        My Reviews
      </h1>
      <p
        style={{ color: "#64748b", marginBottom: 20 }}
        title="Track, continue, or revisit your past evaluations."
      >
        Startups you’ve already evaluated
      </p>

      {/* FILTER BAR */}
      <div
        title="Use filters to quickly find specific reviews."
        style={{
          display: "flex",
          gap: 14,
          marginBottom: 26,
          padding: 16,
          borderRadius: 16,
          background: "linear-gradient(135deg,#ffffff,#f1f5f9)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
        }}
      >
        <input
          type="text"
          placeholder="🔍 Search startup or founder..."
          title="Search by startup name or founder name."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
        />

        <select
          value={industry}
          title="Filter reviews by industry."
          onChange={(e) => setIndustry(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
        >
          <option value="">All industries</option>
          {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
        </select>

        <select
          value={status}
          title="Filter reviews by evaluation status."
          onChange={(e) => setStatus(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
          }}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
        </select>
      </div>

      {!loading && (
        <p
          style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}
          title="Number of reviews matching your current filters."
        >
          Showing {filtered.length} review{filtered.length !== 1 ? "s" : ""}
        </p>
      )}

      {loading ? (
        <p style={{ color: "#64748b" }}>Loading your reviews…</p>
      ) : (
        <>
          {[
            { key: "draft", title: "📝 Draft Reviews", items: draftItems },
            { key: "submitted", title: "✅ Submitted Reviews", items: submittedItems },
          ].map(section =>
            section.items.length > 0 && (
              <div
                key={section.key}
                className="card"
                title="Click to expand or collapse this section."
                style={{ marginBottom: 22, borderRadius: 16, overflow: "hidden" }}
              >
                <div
                  onClick={() =>
                    setOpenSection(openSection === section.key ? null : section.key)
                  }
                  style={{
                    padding: 18,
                    cursor: "pointer",
                    background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <h3 style={{ margin: 0 }}>{section.title}</h3>
                  <span>{openSection === section.key ? "−" : "+"}</span>
                </div>

                {openSection === section.key && (
                  <div style={{ padding: 18, maxHeight: 360, overflowY: "auto" }}>
                    {section.items.map((r) => ReviewCard(r))}
                  </div>
                )}
              </div>
            )
          )}

          {draftItems.length === 0 && submittedItems.length === 0 && (
            <p style={{ color: "#64748b" }}>
              No reviews match your filters.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default MyReviews;
